﻿import { Entity, Not, System } from "ecsy";
import { OnBuy, OnBuyOther, OnStartBattle, OnStartShop, PreviewOnly, VirusLoadIncrease } from "../components";
import { CompositionElement, getEntityPlayer } from "../composition";
import { CellCategory } from "../constants";
import { getCellCategory } from "../elementDisplayInfo";
import { getId } from "../entityFactory";
import { Game, GameState } from "../game";
import { GameElement } from "../gameElementTypes";
import { ClientMessageType } from "../messageTypes";
import { isRewardCell, Player } from "../player";
import { triggerEffect } from "../triggeredEffects";
import { ExecutesEvenIfPaused } from "../utils";


//Takes over frame updates until its "completed"
export class ControlState {
	isComplete = false;
	update(delta: number) { }
	onExit(controller: StateSystem) { }
	onEnter(controller: StateSystem) { }
	onResume(prevState: ControlState) { }
	onMessage(player: Player, message: ClientMessageType) { }
	onCancel() { }
	getName(): GameState {
		return "lobby";
	}
}

type StateStackFrame = {
	state: ControlState;
	entered: boolean;
	resolvePromise?: (value?: unknown) => void;
};

export class StateSystem extends System implements ExecutesEvenIfPaused {
	protected stateStack = new Array<StateStackFrame>();
	private currentState: GameState = "lobby";
	private game: Game;
	private lastStateImmuneCells = new Set<number>();

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
	}

	execute(delta: number) {
		this.executeEvenIfPaused(delta);
	}

	executeEvenIfPaused(delta: number) {
		if (this.stateStack.length === 0) {
			return;
		}

		//State machine
		const thisState = this.peekState();
		if (thisState.isComplete) {
			const frame = this.stateStack[0];
			const completeState = this.popState(false)!;
			console.debug(`Exiting state ${completeState.getName()} complete=${completeState.isComplete} ts=${performance.now()}`);
			completeState.onExit(this);
			if (frame.resolvePromise) frame.resolvePromise();
			if (this.stateStack.length > 0) {
				const nextState = this.peekState();
				if (this.stateStack[0].entered) {
					console.debug(`Resuming state ${nextState.getName()} ts=${performance.now()}`);
					nextState.onResume(completeState);
				}
			}
		}
		if (this.stateStack.length === 0) {
			return;
		}

		while (!this.stateStack[0].entered) {
			this.stateStack[0].entered = true;
			const peeked = this.peekState();
			console.debug(`Entering state ${peeked.getName()} complete=${peeked.isComplete} ts=${performance.now()}`);
			peeked.onEnter(this);
			this.updateLastRoundImmuneCells();
		}

		const currentState = this.peekState();
		currentState.update(delta);

		//Handle queries
		//TODO: Big problem with this one. Also triggeres for bought or in battle created entitities. 
		if (this.queries.onShop.results.length > 0 && this.currentState === 'shop') {
			while (this.queries.onShop.results.length > 0) {
				const entity = this.queries.onShop.results[0];
				const id = getId(entity);
				if (id && this.lastStateImmuneCells.has(id)) {
					const trigger = entity.getComponent(OnStartShop);
					triggerEffect(trigger.effect, entity);
				}
				entity.removeComponent(OnStartShop);
			};
		}
		if (this.queries.onBattle.results.length > 0 && this.currentState === 'simulation') {
			while (this.queries.onBattle.results.length > 0) {
				const entity = this.queries.onBattle.results[0];
				const id = getId(entity);
				if (id && this.lastStateImmuneCells.has(id)) {
					const trigger = entity.getComponent(OnStartBattle);
					triggerEffect(trigger.effect, entity);
				}
				entity.removeComponent(OnStartBattle);
			};
		}

		//Cleanup this component that gets added in either simulation or shop
		while (this.queries.loadIncreaseFlag.results.length > 0) {
			this.queries.loadIncreaseFlag.results[0].removeComponent(VirusLoadIncrease);
		}
	}

	pushState(state: ControlState) {
		this.currentState = state.getName();
		this.stateStack.unshift({ state: state, entered: false });
	}

	resolveState(state: ControlState) {
		return new Promise(resolve => {
			this.stateStack.unshift({
				state: state,
				entered: false,
				resolvePromise: resolve
			});
		});
	}

	popState(triggerCancelEvents = true) {
		if (this.stateStack.length === 0) return null
		const stackFrame = this.stateStack.shift()!
		if (stackFrame.entered && triggerCancelEvents) {
			console.debug(`Cancelling state ${stackFrame.state.getName()} ts=${performance.now()}`);
			stackFrame.state.onCancel();
		}
		return stackFrame.state;
	}

	peekState() {
		return this.stateStack[0].state;
	}

	statesOnStack() {
		return this.stateStack.length;
	}

	getCurrentState(): GameState {
		return this.currentState;
	}

	updateLastRoundImmuneCells() {
		this.lastStateImmuneCells.clear();
		for (const player of this.game.players) {
			for (const cell of player.immuneCells) {
				this.lastStateImmuneCells.add(cell.id);
			}
		};
	}

	//Client/Server shared shop triggers //TODO: I don't really like having this here
	sharedOnBuy(bought: Entity, element: CompositionElement, player: Player) {
		if (bought.hasComponent(OnBuy)) {
			const trigger = bought.getComponent(OnBuy);
			triggerEffect(trigger.effect, bought);
		}

		this.queries.onBuyOther.results.forEach(entity => {
			if (player === getEntityPlayer(entity, this.game)) {
				if (entity === bought) return;
				const trigger = entity.getComponent(OnBuyOther);
				const type = element.type;
				if (trigger.categoryFilter !== CellCategory.None && trigger.categoryFilter !== getCellCategory(element.type)) return;
				if (trigger.typeFilter < GameElement.SHOPABLE_ELEMENT_NUMBER && trigger.typeFilter !== type) return;
				triggerEffect(trigger.effect, entity, bought);
			}
		});

		if (isRewardCell(element.type)) {
			this.game.stats.rewardsBought += 1;
		}
	}
}

StateSystem.queries = {
	onBuyOther: { components: [OnBuyOther, Not(PreviewOnly)] },
	onBattle: { components: [OnStartBattle], listen: { added: true } },
	onShop: { components: [OnStartShop], listen: { added: true } },
	loadIncreaseFlag: { components: [VirusLoadIncrease] },
}